import { useEffect, useState, useCallback } from 'react';

const useInfiniteScroll = (ref, onLoadMore, total, limit) => {
  const [offset, setOffset] = useState(limit);
  const [loading, setLoading] = useState(false);

  const resetOffset = () => {
    setOffset(limit);
  };

  // Separate the scroll handler as useCallback for better performance
  const handleScroll = useCallback(
    async (e) => {
      if (loading) return;

      const element = e.target;
      // More reliable way to detect bottom with a 50px threshold
      const isNearBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 50;

      if (isNearBottom && total > offset && !loading) {
        setLoading(true);

        try {
          await onLoadMore(offset);
          setOffset((prevOffset) => prevOffset + limit);
        } catch (error) {
          console.error('Error loading more items:', error);
        } finally {
          setLoading(false);
        }
      }
    },
    [loading, offset, total, limit, onLoadMore]
  );

  // Force check if we need to load more when component mounts or dependencies change
  useEffect(() => {
    // Only perform this check if we've been given valid inputs
    if (ref.current && total > limit && !loading) {
      const element = ref.current;

      // Check if initial content doesn't fill the container
      if (element.scrollHeight <= element.clientHeight) {
        const loadMore = async () => {
          setLoading(true);
          try {
            await onLoadMore(offset);
            setOffset((prevOffset) => prevOffset + limit);
          } catch (error) {
            console.error('Error initial loading more items:', error);
          } finally {
            setLoading(false);
          }
        };
        loadMore();
      }
    }
  }, [ref, total, limit, offset, loading, onLoadMore]);

  useEffect(() => {
    if (!ref.current) return;

    const div = ref.current;
    div.addEventListener('scroll', handleScroll);

    return () => {
      div.removeEventListener('scroll', handleScroll);
    };
  }, [ref, handleScroll]);

  return [loading, resetOffset];
};

export default useInfiniteScroll;
